import { SCORE, STORAGE_KEYS, TIER_COUNT } from '../config';

export interface HighScoreEntry {
  score: number;
  maxTier: number;
  date: string;
}

export class ScoreSystem {
  score = 0;
  combo = 0;
  comboTimer = 0;
  maxTierReached = 0;
  merges = 0;
  lastGain = 0;
  lastGainTimer = 0;
  chainDepth = 0;

  private readonly comboWindow = 1.6;

  reset(): void {
    this.score = 0;
    this.combo = 0;
    this.comboTimer = 0;
    this.maxTierReached = 0;
    this.merges = 0;
    this.lastGain = 0;
    this.lastGainTimer = 0;
    this.chainDepth = 0;
  }

  update(dt: number): void {
    if (this.comboTimer > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) {
        this.combo = 0;
        this.chainDepth = 0;
      }
    }
    if (this.lastGainTimer > 0) this.lastGainTimer -= dt;
  }

  /** Register a merge into resultTier (the new piece tier) */
  registerMerge(resultTier: number, chainIndex: number): number {
    this.merges++;
    this.maxTierReached = Math.max(this.maxTierReached, resultTier);
    this.combo++;
    this.comboTimer = this.comboWindow;
    this.chainDepth = Math.max(this.chainDepth, chainIndex + 1);

    const base = SCORE.mergeBase[Math.min(resultTier, SCORE.mergeBase.length - 1)] ?? 1;
    const comboMul = 1 + Math.min(this.combo - 1, SCORE.maxComboDisplay) * SCORE.comboMultiplier;
    const chainMul = 1 + chainIndex * SCORE.chainBonus;
    const gain = Math.round(base * comboMul * chainMul);

    this.score += gain;
    this.lastGain = gain;
    this.lastGainTimer = 0.9;
    return gain;
  }

  static loadHighScores(): HighScoreEntry[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.highScores);
      if (!raw) return [];
      const data = JSON.parse(raw) as HighScoreEntry[];
      return Array.isArray(data) ? data.slice(0, 10) : [];
    } catch {
      return [];
    }
  }

  static saveHighScore(entry: HighScoreEntry): HighScoreEntry[] {
    const list = ScoreSystem.loadHighScores();
    list.push(entry);
    list.sort((a, b) => b.score - a.score);
    const top = list.slice(0, 10);
    localStorage.setItem(STORAGE_KEYS.highScores, JSON.stringify(top));
    return top;
  }

  get progressToFinal(): number {
    return this.maxTierReached / (TIER_COUNT - 1);
  }
}
